/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { clone, flatMap, findIndex, uniqBy } from 'lodash'
import React from 'react'
import { useIntl } from 'react-intl'

import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { DeploymentTemplateInfoV2, InstanceConfiguration } from '@modules/cloud-api/v1/types'
import type { NodeConfiguration, ZoneSummaryInstance } from '@modules/ui-types'

import {
  findInstanceByExactMatch,
  findInstanceByPartialMatch,
  getArchitectureDescription,
  getInstancesByType,
  isMatchingInstance,
} from '@/lib/deployments/architecture'
import { getTopologyElementName } from '@/lib/sliders/messages'
import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'
import {
  getVersion,
  getTopology,
  getResources,
} from '@/lib/stackDeployments/selectors/fundamentals'
import { getDeploymentNodeConfigurations } from '@/lib/stackDeployments/selectors/topologyElements'

import messages from '../messages'

import ChangeSummary from './ChangeSummary'
import HardwareSummary from './HardwareSummary'
import { getDiffs } from './hardwareComparisonHelpers'

import type { HardwareComparisonPanelsProps as Props } from './types'

const HardwareComparisonPanels: React.FunctionComponent<Props> = ({
  deployment,
  deploymentTemplates,
  migratedTemplatePayload,
  showDetails = false,
}): JSX.Element => {
  const { formatMessage } = useIntl()
  const version = getVersion({ deployment }) || undefined
  // These will be used for the new hardware as we're getting all the ICs in the available templates
  const templateInstanceConfigurations = uniqBy(
    flatMap<DeploymentTemplateInfoV2, InstanceConfiguration>(
      deploymentTemplates,
      ({ instance_configurations }) => instance_configurations,
    ),
    'id',
  )

  const nodeConfigurations: NodeConfiguration[] = flatMap(
    getResources({ deployment }),
    (resource) => getTopology({ resource }),
  )
  // For the existing hardware we use the ICs returned in the deployment itself
  const { keys: existingHardware } = getArchitectureDescription({
    nodeConfigurations,
    instanceConfigurations: deployment.instance_configurations,
    version,
  })

  const migratedNodeConfigurations = getDeploymentNodeConfigurations({
    deployment: migratedTemplatePayload,
  })
  const { keys: migratedHardware } = getArchitectureDescription({
    nodeConfigurations: migratedNodeConfigurations,
    instanceConfigurations: templateInstanceConfigurations,
    version,
  })

  // Filter out elasticsearch as ES tiers are handled separately
  const sliderTypes = getSupportedSliderInstanceTypes().filter(
    (sliderType) => sliderType !== 'elasticsearch',
  )

  const rows = sliderTypes.reduce(
    (prevInstances, sliderInstanceType) => {
      const existingSliderInstances = getInstancesByType(existingHardware, sliderInstanceType)

      const newInstances = getInstancesByType(migratedHardware, sliderInstanceType)
      const remainingNewInstances = clone(newInstances)

      for (const instance of existingSliderInstances) {
        const instanceExactMatch = findInstanceByExactMatch(newInstances, instance)
        const instancePartialMatch = findInstanceByPartialMatch(newInstances, instance)

        const matchedInstance = instanceExactMatch || instancePartialMatch
        const matchedIndex =
          matchedInstance &&
          findIndex(remainingNewInstances, (remainingInstance) =>
            isMatchingInstance(remainingInstance, matchedInstance),
          )

        if (typeof matchedIndex === `number` && matchedIndex > -1) {
          remainingNewInstances.splice(matchedIndex, 1)
        }
      }

      const existingWithFlag: Array<ZoneSummaryInstance & { isAddition?: boolean }> =
        existingSliderInstances.map((instance) => ({
          ...instance,
          isAddition: false,
        }))

      const newSliderInstances: Array<ZoneSummaryInstance & { isAddition: boolean }> =
        remainingNewInstances.map((instance) => ({
          ...instance,
          isAddition: true,
        }))

      return [...prevInstances, ...existingWithFlag, ...newSliderInstances]
    },
    [] as Array<ZoneSummaryInstance & { isAddition?: boolean }>,
  )

  return (
    <React.Fragment>
      {rows.map((instance) => {
        const topologyElement = nodeConfigurations.find(({ id }) => id === instance.topologyId)

        if (!topologyElement) {
          return null
        }

        const componentName = getTopologyElementName(
          {
            topologyElement,
            sliderInstanceType: instance.sliderInstanceType,
            version,
          },
          formatMessage,
        )

        const newInstances = getInstancesByType(migratedHardware, instance.sliderInstanceType)
        const instanceExactMatch = findInstanceByExactMatch(newInstances, instance)
        const instancePartialMatch = findInstanceByPartialMatch(newInstances, instance)
        const diffs = getDiffs(instance, instancePartialMatch)

        const isElasticsearch = instance.sliderInstanceType === 'elasticsearch'
        const shouldShowFullDetails = isElasticsearch || showDetails

        // Check if there are no meaningful changes (same IC name, version, and no diffs)
        const hasNoChanges =
          instanceExactMatch ||
          (instancePartialMatch &&
            instance.name === instancePartialMatch.name &&
            instance.icConfigVersion === instancePartialMatch.icConfigVersion &&
            diffs &&
            diffs.totalSizeDiff === 0 &&
            diffs.cpuDiff === 0 &&
            diffs.storageDiff === 0)

        return (
          <EuiPanel
            key={`${instance.sliderInstanceType}-${instance.topologyId || instance.id}`}
            hasBorder={true}
            hasShadow={false}
            paddingSize='m'
            color='subdued'
          >
            <EuiFlexGroup alignItems='flex-end' gutterSize='m' justifyContent='spaceBetween'>
              <EuiFlexItem grow={false} style={{ minWidth: '280px' }}>
                <EuiText size='s'>
                  <strong>{componentName}</strong>
                </EuiText>
                {!instance.isAddition && shouldShowFullDetails && (
                  <React.Fragment>
                    <EuiSpacer size='s' />
                    <HardwareSummary instance={instance} topologyElement={topologyElement} />
                  </React.Fragment>
                )}
                {!instance.isAddition && !shouldShowFullDetails && (
                  <React.Fragment>
                    <EuiSpacer size='s' />
                    <EuiText size='s' color='subdued'>
                      {instance.name}{' '}
                      {instance.icConfigVersion !== undefined && (
                        <EuiBadge>v{instance.icConfigVersion}</EuiBadge>
                      )}
                    </EuiText>
                  </React.Fragment>
                )}
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiFlexGroup alignItems='flex-start' gutterSize='m'>
                  <EuiFlexItem grow={false} style={{ marginTop: '28px' }}>
                    {instance.isAddition ? (
                      <EuiIcon type='plus' color='success' />
                    ) : (
                      <EuiIcon type='sortRight' />
                    )}
                  </EuiFlexItem>
                  <EuiFlexItem grow={false} style={{ width: '400px', marginTop: '24px' }}>
                    {instance.isAddition && (
                      <HardwareSummary instance={instance} topologyElement={topologyElement} />
                    )}
                    {!instance.isAddition && shouldShowFullDetails && (
                      <ChangeSummary
                        exact={!!hasNoChanges}
                        partial={instancePartialMatch}
                        diffs={diffs}
                        topologyElement={topologyElement}
                      />
                    )}
                    {!instance.isAddition && !shouldShowFullDetails && hasNoChanges && (
                      <EuiText size='s' color='subdued'>
                        <FormattedMessage {...messages.noChanges} />
                      </EuiText>
                    )}
                    {!instance.isAddition && !shouldShowFullDetails && !hasNoChanges && (
                      <EuiText size='s' color='subdued'>
                        {instancePartialMatch?.name}{' '}
                        {instancePartialMatch?.icConfigVersion !== undefined && (
                          <EuiBadge>v{instancePartialMatch.icConfigVersion}</EuiBadge>
                        )}
                      </EuiText>
                    )}
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPanel>
        )
      })}
    </React.Fragment>
  )
}

export default HardwareComparisonPanels
